import { useRef, useState } from 'react';

import _ from 'lodash';
import { Input, Pagination, Typography } from 'antd';

import { StyledModal, EntityList } from 'common/components';
import ProductThumbnail from 'pages/branded-products/components/card/thumbnail/ProductThumbnail';

import { useQueryProductGrid } from 'hooks';

const RecaptureModal = ({
  visible,
  onCancel,
  onAddProducts,
  existingIds = [],
  isSingleSelect = false,
  packageLevels = [],
}) => {
  const selectedCacheRef = useRef([]);

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());

  const {
    data: { gridData, paging } = {},
    status,
    isFetching,
  } = useQueryProductGrid({
    enabled: visible,
    params: {
      search: { searchText },
      pageIndex,
      pageSize,
      filters: [
        {
          fieldName: 'isOwner',
          value: true,
          filterType: 'Equal',
        },
        {
          fieldName: 'isOverlayProduct',
          value: false,
          filterType: 'Equal',
        },
      ],
      excludedProductIds: existingIds,
      packageLevels,
    },
    keepPreviousData: true,
  });

  const resetState = () => {
    setSearchText('');
    setPageIndex(1);
    setPageSize(20);
    setSelectedIds(new Set());
    selectedCacheRef.current = [];
  };

  const handleSearch = (value) => {
    setPageIndex(1);
    setSearchText(value);
  };

  const handleSelectProduct = (item) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        // In single-select mode replace the current selection instead of adding
        if (isSingleSelect) next.clear();
        next.add(item.id);
      }

      return next;
    });

    selectedCacheRef.current = _.uniqBy(
      [...selectedCacheRef.current, item],
      'id'
    );
  };

  const handleOk = async () => {
    const selectedProducts = selectedCacheRef.current.filter(
      (item) => selectedIds.has(item.id) && !existingIds.includes(item.id)
    );

    if (!selectedProducts.length) {
      handleCancel();
      return;
    }

    onAddProducts?.(selectedProducts);
    handleCancel();
  };

  const handleCancel = () => {
    onCancel();
    resetState();
  };

  const title = 'Select a Product';

  return (
    <StyledModal
      title={title}
      visible={visible}
      width={900}
      bodyStyle={{ height: '80vh', display: 'flex', flexDirection: 'column' }}
      onOk={handleOk}
      onCancel={handleCancel}
      okButtonProps={{ disabled: selectedIds.size === 0 }}
      destroyOnClose
      maskClosable={false}
      zIndex={1020}
    >
      <Typography.Paragraph style={{ marginBottom: 10 }}>
        Search for the barcode of the item you want to recapture, select
        checkbox for that item, then click OK.
      </Typography.Paragraph>
      <Input.Search
        placeholder='Search products...'
        onSearch={handleSearch}
        enterButton
        style={{ width: 400, marginBottom: 10 }}
      />

      <EntityList
        classNameObj={{ 'scroller--y': true }}
        entityList={gridData}
        loading={status === 'loading' || isFetching}
        col={5}
        style={{ flex: 1, minHeight: 0, marginBottom: 10 }}
      >
        {(item) => (
          <ProductThumbnail
            dataDetail={{ ...item, isFavorited: false }} // Don't need to show favorite icon
            selected={selectedIds.has(item.id)}
            imageThumbnail={item?.thumb || item?.thumbnail}
            isProductThumbnail
            isShareBtn
            onClickItemGrid={handleSelectProduct}
            onClickCheckboxItem={handleSelectProduct}
            isShowPackageLevel
            isEditable={false}
          />
        )}
      </EntityList>

      <Pagination
        current={pageIndex}
        pageSize={pageSize}
        total={paging?.totalRecord ?? 0}
        onChange={(page, size) => {
          setPageIndex(page);
          setPageSize(size);
        }}
        showSizeChanger
      />
    </StyledModal>
  );
};

export default RecaptureModal;
